import type {
  CreateExternalUserInput,
  ExternalUser,
  UpdateExternalUserInput,
  UserRole,
} from "@/types/externalUser";

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  agencyId: number;
}

/**
 * Everything T1 needs to create the account.
 *
 * The address is lowercased because that is how it is stored and how it is
 * mangled into an Auth0 identity; a difference in case here would be a
 * different user.
 */
export const buildCreateInput = (
  values: UserFormValues
): CreateExternalUserInput => ({
  email: values.email.trim().toLowerCase(),
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  role: values.role,
  agencyId: values.agencyId,
});

/**
 * Only the fields that actually differ.
 *
 * Sending an unchanged value is not free: an agency write ends the user's live
 * session, and a name write is an upstream call. Email and role are absent
 * because the API refuses both — the address is half the Auth0 identity, and
 * the role selects T1 permission ids set at creation.
 */
export const buildPatchInput = (
  values: UserFormValues,
  user: ExternalUser
): UpdateExternalUserInput => {
  const patch: UpdateExternalUserInput = {};

  const firstName = values.firstName.trim();
  if (firstName !== user.firstName) patch.firstName = firstName;

  const lastName = values.lastName.trim();
  if (lastName !== user.lastName) patch.lastName = lastName;

  if (values.agencyId !== user.agencyId) patch.agencyId = values.agencyId;

  return patch;
};
